// ─────────────────────────────────────────────────────────────────────────────
// Content-script registry of the user's btgpt-linked "AI Conversation" chats.
//
// A module-level Set of `@g.us` group ids that are linked to a btgpt
// conversation. The extension consults it on the fromMe message hot path
// (contentMessageServiceHandlers.handleWaEvent, own-message branch) to decide whether a
// message in a group should run a headless btgpt turn.
//
// Why a plain singleton and not a Redux slice: the only consumer is the WA
// event ingest path, which needs a synchronous membership check with zero React
// coupling. It is hydrated once at post-auth init (GET /btgpt/wa-conversations) and
// refreshed after the user creates a new AI conversation. No MQTT live-sync yet
// — there is no existing content-script precedent for subscribing to per-entity
// btgpt/conversations updates, so we rely on load-on-init + refresh-on-create,
// with the backend re-check as the authoritative guard (design §2 routing seam).
// ─────────────────────────────────────────────────────────────────────────────

import { appConfig } from '@common/config/app.config';

const linkedChatIds = new Set<string>();
/** waChatId → btgpt conversationId, so UI can deep-link to the conversation
 *  page. Populated alongside `linkedChatIds` from the same backend load. */
const conversationIdByChatId = new Map<string, string>();
/** waChatId → the conversation's locked language (ISO-639-1). Populated from the
 *  backend load (only when the conversation has locked its language) AND
 *  optimistically from the first voice note's detected language, so a later
 *  voice note in the same chat skips the on-device language-detection pass. */
const languageByChatId = new Map<string, string>();

/** Listeners notified whenever the linked set/map changes (load or optimistic
 *  add). Lets React surfaces (e.g. the header "open AI conversation" button)
 *  re-render when the registry hydrates after mount. The registry is a plain
 *  singleton, so this is the only reactivity seam. */
const changeListeners = new Set<() => void>();

function notifyChange(): void {
  for (const listener of changeListeners) {
    try {
      listener();
    } catch (err) {
      console.warn('[ai-linked-chats] listener threw', err);
    }
  }
}

/** Subscribe to registry changes. Returns an unsubscribe fn. */
export function subscribeLinkedChats(listener: () => void): () => void {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

/** Lazy store access at CALL time. A static `import store from
 *  '@extension-context-common/store/extension-store'` closes a module-init
 *  cycle (backend-api → backend-api-query-handlers → this file →
 *  extension-store → DBServiceReducers → backend-api) that crashed the content
 *  script at boot ("Cannot access 'DBSessionReducer' before initialization"). */
function getExtensionStore(): { getState(): any } | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('@extension-context-common/store/extension-store').default ?? null;
  } catch (err) {
    console.warn('[ai-linked-chats] extension store unavailable', err);
    return null;
  }
}

/** Bearer + support headers, mirroring btgpt-server-client.getAuthHeaders but
 *  reading the EXTENSION store (content-script realm). */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const state: any = getExtensionStore()?.getState();
    const accessToken = state?.UserReducer?.accessToken;
    const socketId = state?.UserReducer?.socketId;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    if (socketId) headers['Socket'] = socketId;
  } catch (err) {
    console.warn('[ai-linked-chats] could not read auth headers', err);
  }
  return headers;
}

function baseUrl(): string {
  return appConfig.API_ENDPOINT;
}

/** True when a chat is one of the user's linked AI-conversation groups. */
export function isAiLinkedChat(chatId?: string): boolean {
  if (!chatId) return false;
  return linkedChatIds.has(chatId);
}

/** Read-only snapshot of the currently-linked chat ids (for diagnostics/tests). */
export function getLinkedChatIds(): ReadonlySet<string> {
  return linkedChatIds;
}

/** The btgpt conversationId linked to a chat, or undefined if the chat isn't
 *  linked. Use to deep-link to the conversation page. */
export function getAiConversationId(chatId?: string): string | undefined {
  if (!chatId) return undefined;
  return conversationIdByChatId.get(chatId);
}

/** The locked language (ISO-639-1) for a linked chat's conversation, or
 *  undefined if unknown yet. Used as the on-device transcription language hint
 *  to skip the detect pass. */
export function getAiLinkedChatLanguage(chatId?: string): string | undefined {
  if (!chatId) return undefined;
  return languageByChatId.get(chatId);
}

/** Optimistically remember a linked chat's language (e.g. from the first voice
 *  note's detected language) so the NEXT voice note skips detection — before the
 *  backend load reflects the now-locked conversation language. */
export function recordAiLinkedChatLanguage(chatId?: string, language?: string): void {
  if (chatId && language) languageByChatId.set(chatId, language);
}

/** Optimistically add a chat id (e.g. immediately after creating a link) so the
 *  ingest path recognises the group before the next full refresh lands. */
export function addLinkedChatId(chatId?: string, conversationId?: string): void {
  if (!chatId) return;
  linkedChatIds.add(chatId);
  if (conversationId) conversationIdByChatId.set(chatId, conversationId);
  notifyChange();
}

/**
 * Hydrate the linked-chat set from the backend. Replaces the whole set so a
 * chat unlinked elsewhere disappears here too. Best-effort: a failure leaves
 * the previous set intact and logs — the backend re-check is authoritative, so
 * a stale extension set can never cause a wrong turn, only a missed one.
 */
export async function loadLinkedChats(): Promise<void> {
  try {
    const res = await fetch(`${baseUrl()}/btgpt/wa-conversations`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      console.warn('[ai-linked-chats] load failed', res.status);
      return;
    }
    const body = await res.json();
    const links: Array<{ waChatId?: string; conversationId?: string; language?: string }> =
      body?.conversations ?? [];
    linkedChatIds.clear();
    conversationIdByChatId.clear();
    // NOTE: languageByChatId is deliberately NOT cleared — the backend only
    // reports a language once the conversation has LOCKED it, so an optimistic
    // entry recorded from the first voice note must survive a refresh that still
    // returns null for that chat.
    for (const link of links) {
      if (link?.waChatId) {
        linkedChatIds.add(link.waChatId);
        if (link.conversationId) {
          conversationIdByChatId.set(link.waChatId, link.conversationId);
        }
        if (link.language) {
          languageByChatId.set(link.waChatId, link.language);
        }
      }
    }
    console.log(`[ai-linked-chats] loaded ${linkedChatIds.size} linked chat(s)`);
    notifyChange();
  } catch (err) {
    console.warn('[ai-linked-chats] load threw', err);
  }
}

/** Re-fetch the linked-chat set. Alias of loadLinkedChats kept as a distinct
 *  name for call-site intent (after creating a new link). */
export async function refreshLinkedChats(): Promise<void> {
  return loadLinkedChats();
}

// ── Auto-refresh ────────────────────────────────────────────────────────────
// A group can be created OUTSIDE this extension instance — most notably by the
// `ai_conversation` agent tool ("open me a new conversation" from the phone or
// from inside an existing AI group). The optimistic add only covers the local
// button path, so without a refresh the new group's messages would silently
// fail to pierce until the next full page load. Refresh every 5 minutes plus
// whenever the WhatsApp tab regains visibility (the moment a user who created
// a group on their phone is most likely to continue on desktop).
const AUTO_REFRESH_MS = 5 * 60 * 1000;
let autoRefreshStarted = false;

export function startLinkedChatsAutoRefresh(): void {
  if (autoRefreshStarted) return;
  autoRefreshStarted = true;
  setInterval(() => { void loadLinkedChats(); }, AUTO_REFRESH_MS);
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void loadLinkedChats();
    });
  } catch {
    /* non-DOM context — interval alone suffices */
  }
}

export interface CreateAiConversationResult {
  conversationId: string;
  waChatId: string;
  groupName?: string;
}

/**
 * Ask the backend to create a solo AI-conversation group (server-side, via the
 * engine tools), link a btgpt conversation to it, and return the ids. The
 * `{ create: true }` variant does the WA group creation + avatar + link in one
 * shot so it also works as an agent tool from the phone.
 */
export async function createAiConversationLink(
  title?: string,
): Promise<CreateAiConversationResult> {
  const res = await fetch(`${baseUrl()}/btgpt/wa-conversations`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ create: true, title }),
  });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const errBody = await res.json();
      detail = errBody?.error || errBody?.message || detail;
    } catch {
      /* keep status-based message */
    }
    throw new Error(detail);
  }
  const body = await res.json();
  if (!body?.waChatId) {
    throw new Error('Backend did not return a waChatId for the new AI conversation');
  }
  // Optimistic add so the ingest path recognises the group immediately.
  addLinkedChatId(body.waChatId, body.conversationId);
  return {
    conversationId: body.conversationId,
    waChatId: body.waChatId,
    groupName: body.groupName,
  };
}
